import {
  Add,
  AttachMoney,
  Article,
  Delete,
  Domain,
  Edit,
  Inventory2,
  OpenInNew,
  Palette,
  Search,
  Save,
  Settings,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import { FeedbackSnackbar } from "../components/design/FeedbackSnackbar";
import { FilterableDataTable, type FilterableDataTableColumn } from "../components/design/FilterableDataTable";
import { LoadingButton } from "../components/design/LoadingButton";
import { StorageImageCropField } from "../components/design/StorageImageCropField";
import { SectionTabs } from "../components/design/SectionTabs";
import {
  dashboardCardSx,
  dashboardPagePaddingSx,
  dashboardPageSx,
  dashboardSectionTitleSx,
} from "../components/layout/dashboardStyles";
import { travelApi } from "../data/api/TravelApi";
import { useStorageImages } from "../data/providers/StorageProvider";
import { openExperiencePreview } from "../utils/experiencePreview";
import { chatManager } from "../data/managers/ChatManager";
import { MessageTypeEnum } from "../data/models/Chat/MessageModel";
import {
  TravelBookingRequest,
  TravelBookingStatus,
  TravelCategory,
  TravelConfig,
  TravelHomeHighlight,
  TravelPackage,
  TravelAvailabilityRange,
  TravelFlightSchedule,
  TravelLocation,
  TravelService,
  TravelServiceCategory,
  TravelProductCategory,
  TravelVehicleType,
  TravelQuote,
  type ExperienceFontFamily,
} from "../data/models/Experience/ExperienceModels";
import { StoreDomain, StorePageConfig, StorePageId } from "../data/models/Experience/ExperienceSharedModels";
import {
  domainStatusColor,
  experiencePageLabels,
  mergeExperiencePages,
  SnackbarSeverity,
} from "./experienceAdmin/shared";

type TravelTab = "settings" | "design" | "pages" | "seo" | "locations" | "services" | "packages" | "domains" | "bookings";

const basePages: StorePageConfig[] = [
  { id: "home", slug: "", title: "Inicio", navLabel: "Inicio", footerLabel: "Inicio", contentTitle: "Inicio", contentBody: "", showInNavbar: true, showInFooter: false, isEnabled: true },
  { id: "about", slug: "nosotros", title: "Nosotros", navLabel: "Nosotros", footerLabel: "Nosotros", contentTitle: "Nosotros", contentBody: "Cuenta la experiencia, destinos y enfoque de tu agencia.", showInNavbar: true, showInFooter: true, isEnabled: true },
  { id: "contact", slug: "contactanos", title: "Contáctanos", navLabel: "Contáctanos", footerLabel: "Contáctanos", contentTitle: "Contáctanos", contentBody: "Comparte tus canales de atención, horarios y ubicaciones.", showInNavbar: true, showInFooter: true, isEnabled: true },
  { id: "terms", slug: "terminos-y-condiciones", title: "Términos y condiciones", navLabel: "Términos", footerLabel: "Términos y condiciones", contentTitle: "Términos y condiciones", contentBody: "Agrega aquí las condiciones comerciales y políticas de reserva.", showInNavbar: false, showInFooter: true, isEnabled: true },
  { id: "privacy", slug: "politicas-de-privacidad", title: "Políticas de privacidad", navLabel: "Privacidad", footerLabel: "Políticas de privacidad", contentTitle: "Políticas de privacidad", contentBody: "Agrega aquí como recolectas, usas y proteges los datos de tus viajeros.", showInNavbar: false, showInFooter: true, isEnabled: true },
];

const travelSectionCardSx = { ...dashboardCardSx, bgcolor: "background.default" };

const travelWeekdays = [
  { value: 0, label: "Domingo", shortLabel: "Dom" },
  { value: 1, label: "Lunes", shortLabel: "Lun" },
  { value: 2, label: "Martes", shortLabel: "Mar" },
  { value: 3, label: "Miércoles", shortLabel: "Mié" },
  { value: 4, label: "Jueves", shortLabel: "Jue" },
  { value: 5, label: "Viernes", shortLabel: "Vie" },
  { value: 6, label: "Sábado", shortLabel: "Sáb" },
];

const travelFontOptions: { value: ExperienceFontFamily; label: string }[] = [
  { value: "inter", label: "Inter — moderna y equilibrada" },
  { value: "manrope", label: "Manrope — limpia y actual" },
  { value: "dm-sans", label: "DM Sans — cercana y legible" },
  { value: "plus-jakarta-sans", label: "Plus Jakarta Sans — fresca y elegante" },
  { value: "outfit", label: "Outfit — geométrica y dinámica" },
  { value: "montserrat", label: "Montserrat — editorial y viajera" },
  { value: "playfair-display", label: "Playfair Display — elegante y boutique" },
];

const travelProductOptions: { value: TravelProductCategory; label: string }[] = [
  { value: "flight", label: "Vuelos" },
  { value: "hotel", label: "Hoteles" },
  { value: "package", label: "Paquetes" },
];

const travelVehicleOptions: { value: TravelVehicleType; label: string }[] = [
  { value: "small", label: "Pequeño" },
  { value: "sedan", label: "Sedán" },
  { value: "suv", label: "SUV" },
  { value: "van", label: "Van" },
  { value: "minibus", label: "Minibús" },
];

const newAvailabilityRange = (): TravelAvailabilityRange => ({ id: crypto.randomUUID(), startDate: "", endDate: "" });
const newFlightSchedule = (): TravelFlightSchedule => ({ id: crypto.randomUUID(), airlineName: "", scheduleMode: "weekly", departureDate: "", returnDate: "", departureDaysOfWeek: [], returnDaysOfWeek: [], price: 0 });
const defaultHomeHighlights: TravelHomeHighlight[] = [
  { title: "Un destino, muchas formas de vivirlo", description: "Encuentra el viaje que encaja contigo y con tus fechas.", imageUrl: "/images/travel-landscape.jpg" },
  { title: "Descansa donde quieras", description: "Explora hoteles para hacer de cada parada parte del viaje.", imageUrl: "/images/travel-hotel.jpg" },
  { title: "Tu próxima escapada te espera", description: "Inspírate, elige tus opciones y arma una experiencia a tu manera.", imageUrl: "/images/travel-beach.jpg" },
];
const defaultConfig: Partial<TravelConfig> = {
  brandName: "",
  slug: "",
  templateId: "travel-default",
  fontFamily: "inter",
  searchMonths: 3,
  enabledCategories: ["flight", "hotel", "package"],
  heroEyebrow: "Descubre Venezuela a tu manera",
  homeHighlightsEnabled: true,
  homeHighlightsEyebrow: "VIAJA A TU MANERA",
  homeHighlightsTitle: "Tu viaje empieza con un buen plan",
  homeHighlightsDescription: "Explora opciones y recibe confirmación de disponibilidad y precio directamente de tu agencia.",
  homeHighlights: defaultHomeHighlights,
  experienceType: "travel",
  status: "draft",
  pages: basePages,
};

const defaultCategory: Partial<TravelCategory> = {
  name: "",
  description: "",
  isActive: true,
  sortOrder: 0,
};

const defaultPackage: Partial<TravelPackage> = {
  name: "",
  description: "",
  destination: "",
  categoryId: "",
  serviceIds: [],
  priceMode: "fixed",
  priceFrom: 0,
  durationDays: 3,
  durationNights: 2,
  availabilityRanges: [newAvailabilityRange()],
  imageUrl: "",
  includes: [],
  excludes: [],
  availabilityNotes: "",
  capacityNotes: "",
  isActive: true,
  sortOrder: 0,
};

const tabPaths: Record<TravelTab, string> = {
  settings: "/dashboard/travel/settings",
  design: "/dashboard/travel/design",
  pages: "/dashboard/travel/pages",
  seo: "/dashboard/travel/seo",
  locations: "/dashboard/travel/locations",
  services: "/dashboard/travel/services",
  packages: "/dashboard/travel/packages",
  domains: "/dashboard/travel/domains",
  bookings: "/dashboard/travel/bookings",
};

const operationalTravelTabs: TravelTab[] = ["locations", "services", "packages", "bookings"];

const operationalTravelTitles: Partial<Record<TravelTab, string>> = {
  locations: "Rutas y ciudades",
  services: "Ofertas de viaje",
  packages: "Paquetes",
  bookings: "Reservas de viajes",
};

const operationalTravelDescriptions: Partial<Record<TravelTab, string>> = {
  locations: "Administra las ciudades disponibles como origen, destino y lugar de servicio.",
  services: "Configura vuelos y hoteles para las búsquedas y los paquetes de tus viajeros.",
  packages: "Solo los vuelos y hoteles activos pueden combinarse para las fechas elegidas.",
  bookings: "Revisa las solicitudes de los viajeros, prepara cotizaciones y organiza su atención.",
};

const bookingStatusLabels: Record<TravelBookingStatus, string> = {
  pending: "Pendiente",
  contacted: "Contactado",
  confirmed: "Confirmado",
  quoted: "Cotizado",
  changes_requested: "Cambios solicitados",
  cancelled: "Cancelado",
};

const TravelPage = ({ initialTab = "settings" }: { initialTab?: TravelTab }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TravelTab>(initialTab);
  const isOperationalTab = operationalTravelTabs.includes(tab);
  const [config, setConfig] = useState<Partial<TravelConfig>>(defaultConfig);
  const experienceStorage = useStorageImages(config.companyId, "experience-sites");
  const [categories, setCategories] = useState<TravelCategory[]>([]);
  const [locations, setLocations] = useState<TravelLocation[]>([]);
  const [services, setServices] = useState<TravelService[]>([]);
  const [packages, setPackages] = useState<TravelPackage[]>([]);
  const [domains, setDomains] = useState<StoreDomain[]>([]);
  const [previewAuthorizations, setPreviewAuthorizations] = useState<Record<string, { token: string; whatsappNumber: string }>>({});
  const [bookings, setBookings] = useState<TravelBookingRequest[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<StorePageId>("home");
  const [categoryDraft, setCategoryDraft] = useState<Partial<TravelCategory>>(defaultCategory);
  const [packageDraft, setPackageDraft] = useState<Partial<TravelPackage>>(defaultPackage);
  const [categoryEditingId, setCategoryEditingId] = useState<string | undefined>();
  const [packageEditingId, setPackageEditingId] = useState<string | undefined>();
  const [domainDraft, setDomainDraft] = useState("");
  const [loadError, setLoadError] = useState("");
  const [loadingData, setLoadingData] = useState(true);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const activeActionRef = useRef<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>("success");
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<TravelCategory | undefined>();
  const [locationDialog, setLocationDialog] = useState(false);
  const [locationDraft, setLocationDraft] = useState<Partial<TravelLocation>>({ country: "Venezuela", city: "", isOrigin: true, isDestination: true, isActive: true });
  const [editingLocationId, setEditingLocationId] = useState<string>();
  const [serviceDialog, setServiceDialog] = useState(false);
  const [serviceDraft, setServiceDraft] = useState<Partial<TravelService>>({ category: "flight", name: "", description: "", price: 0, flightSchedules: [newFlightSchedule()], isActive: true });
  const [editingServiceId, setEditingServiceId] = useState<string>();
  const [packageToDelete, setPackageToDelete] = useState<TravelPackage | undefined>();
  const [quoteBooking, setQuoteBooking] = useState<TravelBookingRequest | undefined>();
  const [quoteDraft, setQuoteDraft] = useState<TravelQuote>({ items: [], adjustmentLabel: "", adjustmentAmount: 0, discountAmount: 0, notes: "", total: 0, currency: "USD", status: "draft" });
  const [sendQuoteAfterSave, setSendQuoteAfterSave] = useState(false);

  const pages = mergeExperiencePages(basePages, config.pages);
  const defaultPreviewHost = String(config.primaryDomain || `${config.slug || "preview"}.store.lyproai.com`).trim().toLowerCase();
  const previewHostsKey = Array.from(new Set([defaultPreviewHost, ...domains.map((domain) => domain.hostname.trim().toLowerCase())].filter(Boolean))).join("|");
  const selectedPage = pages.find((page) => page.id === selectedPageId) || pages[0];
  const quotePreviewTotal = Math.max(
    0,
    quoteDraft.items.reduce((sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0), 0) +
      Number(quoteDraft.adjustmentAmount || 0) - Number(quoteDraft.discountAmount || 0),
  );

  const showSnackbar = (message: string, severity: SnackbarSeverity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const runWithLoading = async <T,>(key: string, errorMessage: string, action: () => Promise<T>): Promise<T | undefined> => {
    if (activeActionRef.current) return undefined;
    activeActionRef.current = key;
    setActiveAction(key);
    try {
      return await action();
    } catch {
      showSnackbar(`${errorMessage} Intenta de nuevo.`, "error");
      return undefined;
    } finally {
      activeActionRef.current = null;
      setActiveAction(null);
    }
  };

  const updateSelectedPage = (patch: Partial<StorePageConfig>) => {
    setConfig((current) => ({
      ...current,
      pages: mergeExperiencePages(basePages, current.pages).map((page) =>
        page.id === selectedPageId ? { ...page, ...patch } : page,
      ),
    }));
  };

  const resetCategoryDialog = () => {
    setCategoryEditingId(undefined);
    setCategoryDraft(defaultCategory);
    setCategoryDialogOpen(false);
  };

  const resetPackageDialog = () => {
    setPackageEditingId(undefined);
    setPackageDraft(defaultPackage);
    setPackageDialogOpen(false);
  };

  const load = async () => {
    setLoadingData(true);
    setLoadError("");
    try {
      const [configResult, categoriesResult, locationsResult, servicesResult, packagesResult, domainsResult, bookingsResult] = await Promise.all([
        travelApi.config.requestHard(null),
        travelApi.categories.requestHard(null),
        travelApi.locations.requestHard(null),
        travelApi.services.requestHard(null),
        travelApi.packages.requestHard(null),
        travelApi.domains.requestHard(null),
        travelApi.bookings.requestHard(null),
      ]);

      if (configResult.isSuccess && configResult.data) {
        setConfig({
          ...defaultConfig,
          ...configResult.data,
          pages: mergeExperiencePages(basePages, configResult.data.pages),
        });
      }
      if (categoriesResult.isSuccess) setCategories(categoriesResult.data || []);
      if (locationsResult.isSuccess) setLocations(locationsResult.data || []);
      if (servicesResult.isSuccess) setServices(servicesResult.data || []);
      if (packagesResult.isSuccess) setPackages(packagesResult.data || []);
      if (domainsResult.isSuccess) setDomains(domainsResult.data || []);
      if (bookingsResult.isSuccess) setBookings(bookingsResult.data || []);

      setLoadError(
        configResult.messageError ||
          categoriesResult.messageError ||
          locationsResult.messageError ||
          servicesResult.messageError ||
          packagesResult.messageError ||
          domainsResult.messageError ||
          bookingsResult.messageError ||
          "",
      );
    } catch {
      setLoadError("No pudimos cargar la información de Travel. Revisa tu conexión e inténtalo de nuevo.");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => setTab(initialTab), [initialTab]);

  useEffect(() => {
    let active = true;
    setPreviewAuthorizations({});
    const hostnames = previewHostsKey.split("|").filter(Boolean);
    void Promise.all(hostnames.map(async (hostname) => {
      try {
        const result = await travelApi.previewToken.requestHard({ hostname });
        return result.isSuccess && result.data?.token
          ? [hostname, { token: result.data.token, whatsappNumber: result.data.whatsappNumber || "" }] as const
          : undefined;
      } catch {
        return undefined;
      }
    })).then((results) => {
      if (!active) return;
      setPreviewAuthorizations(Object.fromEntries(results.filter((item): item is NonNullable<typeof item> => Boolean(item))));
    });
    return () => { active = false; };
  }, [previewHostsKey]);

  const openTravelPreview = (hostname = defaultPreviewHost) => {
    const host = hostname.trim().toLowerCase();
    const authorization = previewAuthorizations[host];
    const opened = openExperiencePreview("travel", { config, categories, locations, services, packages }, host, authorization);
    if (!opened) showSnackbar("El navegador bloqueó la ventana de vista previa. Permite las ventanas emergentes de LYPRO e inténtalo otra vez.", "error");
  };

  const saveConfig = async (publish = false) => {
    await runWithLoading(publish ? "publish" : "config-save", publish ? "No pudimos publicar la página." : "No pudimos guardar la configuración.", async () => {
      const result = publish
        ? await travelApi.publish.requestHard({ config })
        : await travelApi.updateConfig.requestHard({ config });
      if (!result.isSuccess) {
        showSnackbar(result.messageError || (publish ? "No pudimos publicar la página." : "No pudimos guardar la configuración."), "error");
        return;
      }
      showSnackbar(publish ? "Mi agencia de viajes publicada." : "Configuración guardada.", "success");
      await load();
    });
  };

  const saveCategory = async () => {
    if (!String(categoryDraft.name || "").trim()) {
      showSnackbar("El nombre de la categoría es requerido.", "error");
      return;
    }

    await runWithLoading("category-save", "No pudimos guardar la categoría.", async () => {
      const result = categoryEditingId
        ? await travelApi.updateCategory(categoryEditingId).requestHard({ category: categoryDraft })
        : await travelApi.createCategory.requestHard({ category: categoryDraft });

      if (!result.isSuccess) {
        showSnackbar(result.messageError || "No pudimos guardar la categoría.", "error");
        return;
      }

      showSnackbar(categoryEditingId ? "Categoría actualizada." : "Categoría creada.", "success");
      resetCategoryDialog();
      await load();
    });
  };

  const saveLocation = async () => {
    await runWithLoading("location-save", "No pudimos guardar la ciudad.", async () => {
      const result = editingLocationId
        ? await travelApi.updateLocation(editingLocationId).requestHard({ location: locationDraft })
        : await travelApi.createLocation.requestHard({ location: locationDraft });
      if (!result.isSuccess) { showSnackbar(result.messageError || "No pudimos guardar la ciudad.", "error"); return; }
      setLocationDialog(false); setEditingLocationId(undefined); showSnackbar("Ciudad guardada.", "success"); await load();
    });
  };

  const saveService = async () => {
    await runWithLoading("service-save", "No pudimos guardar la oferta.", async () => {
      const result = editingServiceId
        ? await travelApi.updateService(editingServiceId).requestHard({ service: serviceDraft })
        : await travelApi.createService.requestHard({ service: serviceDraft });
      if (!result.isSuccess) { showSnackbar(result.messageError || "No pudimos guardar la oferta.", "error"); return; }
      setServiceDialog(false); setEditingServiceId(undefined); showSnackbar("Oferta guardada.", "success"); await load();
    });
  };

  const removeLocation = async (location: TravelLocation) => {
    await runWithLoading(`location-delete:${location.id}`, "No pudimos eliminar la ciudad.", async () => {
      const result = await travelApi.deleteLocation(location.id).requestHard(null);
      if (!result.isSuccess) { showSnackbar(result.messageError || "No pudimos eliminar la ciudad.", "error"); return; }
      showSnackbar("Ciudad eliminada.", "success"); await load();
    });
  };

  const removeService = async (service: TravelService) => {
    await runWithLoading(`service-delete:${service.id}`, "No pudimos eliminar la oferta.", async () => {
      const result = await travelApi.deleteService(service.id).requestHard(null);
      if (!result.isSuccess) { showSnackbar(result.messageError || "No pudimos eliminar la oferta.", "error"); return; }
      showSnackbar("Oferta eliminada.", "success"); await load();
    });
  };

  const savePackage = async () => {
    if (!String(packageDraft.name || "").trim()) {
      showSnackbar("El nombre del paquete es requerido.", "error");
      return;
    }
    if (!String(packageDraft.destination || "").trim()) {
      showSnackbar("El destino del paquete es requerido.", "error");
      return;
    }

    const payload = {
      ...packageDraft,
      includes: String(packageDraft.includes || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      excludes: String(packageDraft.excludes || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    };

    await runWithLoading("package-save", "No pudimos guardar el paquete.", async () => {
      const result = packageEditingId
        ? await travelApi.updatePackage(packageEditingId).requestHard({ travelPackage: payload })
        : await travelApi.createPackage.requestHard({ travelPackage: payload });

      if (!result.isSuccess) {
        showSnackbar(result.messageError || "No pudimos guardar el paquete.", "error");
        return;
      }

      showSnackbar(packageEditingId ? "Paquete actualizado." : "Paquete creado.", "success");
      resetPackageDialog();
      await load();
    });
  };

  const deleteCategory = async () => {
    if (!categoryToDelete) return;
    await runWithLoading("category-delete", "No pudimos eliminar la categoría.", async () => {
      const result = await travelApi.deleteCategory(categoryToDelete.id).requestHard(null);
      if (!result.isSuccess) {
        showSnackbar(result.messageError || "No pudimos eliminar la categoría.", "error");
        return;
      }
      setCategoryToDelete(undefined);
      showSnackbar("Categoría eliminada.", "success");
      await load();
    });
  };

  const deletePackage = async () => {
    if (!packageToDelete) return;
    await runWithLoading("package-delete", "No pudimos eliminar el paquete.", async () => {
      const result = await travelApi.deletePackage(packageToDelete.id).requestHard(null);
      if (!result.isSuccess) {
        showSnackbar(result.messageError || "No pudimos eliminar el paquete.", "error");
        return;
      }
      setPackageToDelete(undefined);
      showSnackbar("Paquete eliminado.", "success");
      await load();
    });
  };

  const addDomain = async () => {
    if (!domainDraft.trim()) {
      showSnackbar("El dominio es requerido.", "error");
      return;
    }
    await runWithLoading("domain-add", "No pudimos agregar el dominio.", async () => {
      const result = await travelApi.addDomain.requestHard({ hostname: domainDraft.trim() });
      if (!result.isSuccess) {
        showSnackbar(result.messageError || "No pudimos agregar el dominio.", "error");
        return;
      }
      setDomainDraft("");
      setDomainDialogOpen(false);
      showSnackbar("Dominio agregado. Revisa las instrucciones DNS.", "success");
      await load();
    });
  };

  const verifyDomain = async (hostname: string) => {
    await runWithLoading(`domain-verify:${hostname}`, "No pudimos verificar el dominio.", async () => {
      const result = await travelApi.verifyDomain(hostname).requestHard(null);
      if (!result.isSuccess) {
        showSnackbar(result.messageError || "No pudimos verificar el dominio.", "error");
        return;
      }
      showSnackbar(
        result.data?.status === "active" ? "Dominio verificado." : "Aún no encontramos el DNS esperado.",
        result.data?.status === "active" ? "success" : "error",
      );
      await load();
    });
  };

  const updateBookingStatus = async (bookingId: string, status: TravelBookingStatus) => {
    await runWithLoading(`booking-status:${bookingId}`, "No pudimos actualizar la solicitud.", async () => {
      const result = await travelApi.updateBookingStatus(bookingId).requestHard({ status });
      if (!result.isSuccess) {
        showSnackbar(result.messageError || "No pudimos actualizar la solicitud.", "error");
        return;
      }
      showSnackbar("Estado de la solicitud actualizado.", "success");
      await load();
    });
  };

  const openQuoteDialog = (booking: TravelBookingRequest) => {
    const service = services.find((item) => item.id === (booking.productId || booking.packageId));
    const travelPackage = packages.find((item) => item.id === (booking.productId || booking.packageId));
    const quantity = service?.priceUnit === "per_person" ? booking.travelers : service ? 1 : booking.travelers;
    const price = Number((booking.productCategory === "flight" ? booking.flightPrice : undefined) ?? service?.price ?? travelPackage?.priceFrom ?? 0);
    const bundleQuoteItems = (booking.bundleItems || []).map((item) => ({
      id: item.serviceId,
      description: item.category === "flight" && item.airlineName ? `${item.name} · ${item.airlineName}` : item.name,
      quantity: item.quantity,
      unitPrice: item.price,
    }));
    setQuoteBooking(booking);
    setSendQuoteAfterSave(false);
    setQuoteDraft(booking.quote ? { ...booking.quote, status: "draft", sentAt: undefined } : {
      items: bundleQuoteItems.length ? bundleQuoteItems : [{ id: "product", description: booking.packageName, quantity, unitPrice: price }],
      adjustmentLabel: "", adjustmentAmount: 0, discountAmount: 0, notes: "", total: 0, currency: "USD", status: "draft",
    });
  };

  const saveQuote = async () => {
    if (!quoteBooking) return;
    await runWithLoading("quote-save", sendQuoteAfterSave ? "No pudimos enviar la cotización." : "No pudimos guardar la cotización.", async () => {
      const result = await travelApi.updateBookingQuote(quoteBooking.id).requestHard({ quote: quoteDraft });
      if (!result.isSuccess || !result.data) {
        showSnackbar(result.messageError || "No pudimos guardar la cotización.", "error");
        return;
      }
      if (sendQuoteAfterSave) {
        const quote = result.data.quote;
        const subtotal = (quote?.items || []).reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
        const total = Math.max(0, subtotal + Number(quote?.adjustmentAmount || 0) - Number(quote?.discountAmount || 0));
        const lines = (quote?.items || []).map((item) => `- ${item.description}: $${(item.quantity * item.unitPrice).toFixed(2)}`).join("\n");
        const message = `Cotización para la solicitud ${quoteBooking.publicReference}\n${lines}${quote?.adjustmentLabel ? `\n${quote.adjustmentLabel}: $${Number(quote.adjustmentAmount || 0).toFixed(2)}` : ""}${Number(quote?.discountAmount || 0) ? `\nDescuento: -$${Number(quote?.discountAmount || 0).toFixed(2)}` : ""}\nTotal: $${total.toFixed(2)} USD${quote?.notes ? `\n${quote.notes}` : ""}`;
        const sent = await chatManager.sendMessage({ phoneNumber: quoteBooking.customerPhone, type: MessageTypeEnum.TEXT, message });
        if (!sent.sent) {
          showSnackbar(sent.requiresTemplate ? "La ventana de WhatsApp cerró. Usa una plantilla aprobada para enviar esta cotización." : sent.errorMessage || "La cotización quedó guardada, pero no se pudo enviar por WhatsApp.", "error");
          await load();
          return;
        }
        const markedSent = await travelApi.updateBookingQuote(quoteBooking.id).requestHard({ quote: { ...quoteDraft, status: "sent", sentAt: new Date().toISOString() } });
        if (!markedSent.isSuccess) {
          showSnackbar("WhatsApp envió la cotización, pero Lypro no pudo registrar el estado de envío. Actualiza la solicitud.", "error");
          await load();
          return;
        }
      }
      setQuoteBooking(undefined);
      showSnackbar(sendQuoteAfterSave ? "Cotización enviada por WhatsApp." : "Cotización guardada.", "success");
      await load();
    });
  };

  return (
    <Box sx={dashboardPageSx}>
      <Stack spacing={2} sx={dashboardPagePaddingSx}>
        <SectionTabs
          value={tab}
          ariaLabel={isOperationalTab ? "Gestión de viajes" : "Configuración de Mi agencia de viajes"}
          options={isOperationalTab
            ? [
              { value: "locations", label: "Rutas y ciudades", icon: <Domain fontSize="small" /> },
              { value: "services", label: "Ofertas", icon: <AttachMoney fontSize="small" /> },
              { value: "packages", label: "Paquetes", icon: <Inventory2 fontSize="small" /> },
              { value: "bookings", label: "Reservas", icon: <AttachMoney fontSize="small" /> },
            ]
            : [
              { value: "settings", label: "General", icon: <Settings fontSize="small" /> },
              { value: "design", label: "Diseño", icon: <Palette fontSize="small" /> },
              { value: "pages", label: "Páginas", icon: <Article fontSize="small" /> },
              { value: "seo", label: "SEO", icon: <Search fontSize="small" /> },
              { value: "domains", label: "Dominios", icon: <Domain fontSize="small" /> },
            ]}
          onChange={(value) => {
            const nextTab = value as TravelTab;
            setTab(nextTab);
            navigate(tabPaths[nextTab]);
          }}
        />

        <Card sx={{ ...dashboardCardSx, borderRadius: 1.5 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, "&:last-child": { pb: { xs: 1.5, sm: 2.5 } } }}>
            <Stack spacing={2.5}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
          <Box>
            <Typography variant="h4" sx={dashboardSectionTitleSx}>
              {isOperationalTab ? operationalTravelTitles[tab] : "Mi agencia de viajes"}
            </Typography>
            <Typography color="text.secondary">
              {isOperationalTab
                ? operationalTravelDescriptions[tab]
                : "Administra la página, el diseño y los canales de atención de tu agencia."}
            </Typography>
          </Box>
          {!isOperationalTab && <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              disabled={loadingData || Boolean(activeAction)}
              onClick={() => openTravelPreview()}
            >
              Vista previa
            </Button>
            <LoadingButton loading={activeAction === "config-save"} disabled={Boolean(activeAction && activeAction !== "config-save")} variant="outlined" startIcon={<Save />} onClick={() => void saveConfig(false)} loadingLabel="Guardando...">
              Guardar
            </LoadingButton>
            <LoadingButton loading={activeAction === "publish"} disabled={Boolean(activeAction && activeAction !== "publish")} variant="contained" onClick={() => void saveConfig(true)} loadingLabel="Publicando...">
              Publicar
            </LoadingButton>
          </Stack>}
        </Stack>

        {loadingData || activeAction ? <Box role="status" aria-live="polite"><Typography variant="caption" color="text.secondary">{loadingData ? "Actualizando la información de Travel..." : "Procesando tu acción..."}</Typography><LinearProgress aria-label={loadingData ? "Cargando datos de Travel" : "Procesando acción de Travel"} /></Box> : null}
        {loadError ? <Alert severity="error" action={<LoadingButton size="small" loading={activeAction === "data-reload"} disabled={Boolean(activeAction && activeAction !== "data-reload")} onClick={() => void runWithLoading("data-reload", "No pudimos cargar la información de Travel.", load)} loadingLabel="Cargando...">Reintentar</LoadingButton>}>{loadError}</Alert> : null}

        {tab === "settings" && (
          <Stack spacing={2}>
          <Card sx={travelSectionCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6">Identidad, portada y contacto</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Esta información aparece en la página pública. En cada campo te indicamos si se muestra en el encabezado, la portada o el pie de página.
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Nombre de la agencia" helperText="Se usa como texto de derechos reservados y como respaldo del logo del pie si no escribes un texto propio o no cargas un logo." value={config.brandName || ""} onChange={(event) => setConfig({ ...config, brandName: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Subdominio LYPRO" helperText={`La página quedará en ${config.slug || "tu-agencia"}.store.lyproai.com. No escribas https:// ni un dominio propio aquí; los dominios propios se agregan en Dominios.`} value={config.slug || ""} onChange={(event) => setConfig({ ...config, slug: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Texto sobre el título de la portada" helperText="Aparece como una frase breve encima del título principal, sin icono. Déjalo vacío para ocultarlo." inputProps={{ maxLength: 80 }} value={config.heroEyebrow || ""} onChange={(event) => setConfig({ ...config, heroEyebrow: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Título principal de la portada" helperText="Es el texto grande del banner de inicio. Ejemplo: ‘Tu próxima aventura comienza aquí’." value={config.heroTitle || ""} onChange={(event) => setConfig({ ...config, heroTitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Descripción de la portada" helperText="Aparece debajo del título principal. Explica brevemente qué destinos o servicios ofrece tu agencia." value={config.heroSubtitle || ""} onChange={(event) => setConfig({ ...config, heroSubtitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Número WhatsApp (respaldo)" helperText="Este número no conecta WhatsApp. Para activar el botón de atención y recibir solicitudes en LYPRO, conecta el canal en Configuración › WhatsApp Cloud API." value={config.contactWhatsapp || ""} onChange={(event) => setConfig({ ...config, contactWhatsapp: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Correo de contacto" helperText="Se mostrará como enlace en el pie de página para que el viajero pueda escribirte." value={config.contactEmail || ""} onChange={(event) => setConfig({ ...config, contactEmail: event.target.value })} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Texto de derechos reservados" helperText="Aparece centrado al final del pie. Si lo dejas vacío, LYPRO genera el texto con el nombre de la agencia." value={config.footerText || ""} onChange={(event) => setConfig({ ...config, footerText: event.target.value })} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Redes sociales del pie</Typography>
                    <Typography variant="body2" color="text.secondary">Agrega los enlaces de los perfiles de tu agencia. Solo aparecerán iconos para los perfiles que configures.</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Instagram" placeholder="https://instagram.com/tuagencia" value={config.socialLinks?.instagram || ""} onChange={(event) => setConfig({ ...config, socialLinks: { ...config.socialLinks, instagram: event.target.value } })} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Facebook" placeholder="https://facebook.com/tuagencia" value={config.socialLinks?.facebook || ""} onChange={(event) => setConfig({ ...config, socialLinks: { ...config.socialLinks, facebook: event.target.value } })} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="YouTube" placeholder="https://youtube.com/@tuagencia" value={config.socialLinks?.youtube || ""} onChange={(event) => setConfig({ ...config, socialLinks: { ...config.socialLinks, youtube: event.target.value } })} />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
          <Card sx={travelSectionCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6">Buscador y categorías</Typography>
                  <Typography variant="body2" color="text.secondary">Activa las pestañas que ofrecerá tu agencia y limita con cuánta anticipación se pueden buscar fechas.</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField select fullWidth label="Meses máximos para buscar" helperText="El calendario solo permitirá fechas desde hoy hasta este límite." value={config.searchMonths || 3} onChange={(event) => setConfig({ ...config, searchMonths: Number(event.target.value) })}>
                      {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => <MenuItem key={month} value={month}>{month} {month === 1 ? "mes" : "meses"}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Pestañas del buscador</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Puedes ofrecer Vuelos y Hoteles como búsquedas independientes. También se pueden combinar en Paquetes; Carros y Posadas están desactivados por ahora.</Typography>
                    <Grid container spacing={1}>
                      {travelProductOptions.map((item) => {
                        const enabledCategories = (config.enabledCategories || ["flight", "hotel", "package"]).filter((category) => category === "flight" || category === "hotel" || category === "package");
                        const enabled = enabledCategories.includes(item.value);
                        return <Grid item xs={12} sm={6} md={4} key={item.value}>
                          <FormControlLabel control={<Checkbox checked={enabled} onChange={(event) => {
                            const current = (config.enabledCategories || ["flight", "hotel", "package"]).filter((category) => category === "flight" || category === "hotel" || category === "package");
                            const next = event.target.checked ? [...current, item.value] : current.filter((category) => category !== item.value);
                            setConfig({ ...config, enabledCategories: next });
                          }} />} label={item.label} />
                        </Grid>;
                      })}
                    </Grid>
                    {!((config.enabledCategories || ["flight", "hotel", "package"]).filter((category) => category === "flight" || category === "hotel" || category === "package")).length ? <Alert severity="warning">Activa al menos una categoría para mostrar el buscador.</Alert> : null}
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
          </Stack>
        )}

        {tab === "design" && (
          <Stack spacing={2}>
            <Card sx={travelSectionCardSx}>
              <CardContent>
                <Stack spacing={2.5}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Palette fontSize="small" />
                      <Typography variant="h6">Marca y navegación</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Configura el logo del menú, el logo del pie y lo que aparece en la pestaña del navegador.
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <StorageImageCropField
                        label="Logo de la barra superior"
                        helperText="Aparece en el menú de arriba. El nombre del negocio no se repite junto al logo. Recorte horizontal."
                        value={config.logoUrl}
                        aspectRatio={3}
                        uploading={experienceStorage.loading}
                        uploadProgress={experienceStorage.progress}
                        onUpload={(file) => experienceStorage.uploadAssetAndGetUrl(file)}
                        onChange={(logoUrl) => setConfig({ ...config, logoUrl })}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <StorageImageCropField
                        label="Logo del pie de página"
                        helperText="Aparece abajo, junto al texto de derechos reservados. Si no subes uno, se usa el logo de la barra superior. Recorte horizontal."
                        value={config.footerLogoUrl}
                        aspectRatio={3}
                        uploading={experienceStorage.loading}
                        uploadProgress={experienceStorage.progress}
                        onUpload={(file) => experienceStorage.uploadAssetAndGetUrl(file)}
                        onChange={(footerLogoUrl) => setConfig({ ...config, footerLogoUrl })}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <StorageImageCropField
                        label="Ícono de la pestaña (favicon)"
                        helperText="Icono pequeño que aparece junto al título en la pestaña del navegador. Recorte cuadrado."
                        value={config.faviconUrl}
                        aspectRatio={1}
                        uploading={experienceStorage.loading}
                        uploadProgress={experienceStorage.progress}
                        onUpload={(file) => experienceStorage.uploadAssetAndGetUrl(file)}
                        onChange={(faviconUrl) => setConfig({ ...config, faviconUrl })}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Texto de la pestaña del navegador" helperText="Texto que aparecerá junto al favicon en la pestaña. Si lo dejas vacío, se usa el nombre del negocio." value={config.tabTitle || ""} onChange={(event) => setConfig({ ...config, tabTitle: event.target.value })} />
                    </Grid>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>
            <Card sx={travelSectionCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6">Tipografía</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Elige el estilo de letra para títulos, navegación, ofertas y buscador. Puedes revisar el resultado en Vista previa.
                    </Typography>
                  </Box>
                  <TextField
                    select
                    fullWidth
                    label="Tipo de letra"
                    value={config.fontFamily || "inter"}
                    onChange={(event) => setConfig({ ...config, fontFamily: event.target.value as ExperienceFontFamily })}
                    sx={{ maxWidth: { md: 560 } }}
                  >
                    {travelFontOptions.map((font) => <MenuItem key={font.value} value={font.value}>{font.label}</MenuItem>)}
                  </TextField>
                </Stack>
              </CardContent>
            </Card>
            <Card sx={travelSectionCardSx}>
              <CardContent>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="h6">Colores y portada</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Elige los colores de la página y la fotografía principal que verá el viajero.
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField fullWidth type="color" label="Color principal" InputLabelProps={{ shrink: true }} helperText="Acentos y elementos destacados." value={config.primaryColor || "#0c3c60"} onChange={(event) => setConfig({ ...config, primaryColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField fullWidth type="color" label="Color secundario" InputLabelProps={{ shrink: true }} helperText="Se combina con el principal en la portada." value={config.secondaryColor || "#f59e0b"} onChange={(event) => setConfig({ ...config, secondaryColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField fullWidth type="color" label="Fondo de la página" InputLabelProps={{ shrink: true }} helperText="Fondo detrás del contenido y las ofertas." value={config.backgroundColor || "#f5f3ee"} onChange={(event) => setConfig({ ...config, backgroundColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField fullWidth type="color" label="Color del texto" InputLabelProps={{ shrink: true }} helperText="Color base de los textos de la página." value={config.textColor || "#132033"} onChange={(event) => setConfig({ ...config, textColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mt: 0.5 }}>Colores del pie de página</Typography>
                      <Typography variant="body2" color="text.secondary">Elige el fondo y el color del contenido del pie. Comprueba que haya buen contraste en Vista previa.</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField fullWidth type="color" label="Fondo del pie" InputLabelProps={{ shrink: true }} helperText="Fondo de las columnas, redes y derechos reservados." value={config.footerBackgroundColor || "#111313"} onChange={(event) => setConfig({ ...config, footerBackgroundColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField fullWidth type="color" label="Texto del pie" InputLabelProps={{ shrink: true }} helperText="Logo de texto, títulos, enlaces e iconos." value={config.footerTextColor || "#f4f7f7"} onChange={(event) => setConfig({ ...config, footerTextColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12}>
                      <StorageImageCropField
                        label="Imagen de portada"
                        helperText="Foto ancha que aparece detrás del título principal en Inicio. Recorte panorámico 16:6."
                        value={config.heroImageUrl}
                        aspectRatio={16 / 6}
                        uploading={experienceStorage.loading}
                        uploadProgress={experienceStorage.progress}
                        onUpload={(file) => experienceStorage.uploadAssetAndGetUrl(file)}
                        onChange={(heroImageUrl) => setConfig({ ...config, heroImageUrl })}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>
            <Card sx={travelSectionCardSx}>
              <CardContent>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="h6">Contenido entre el buscador y el pie</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Inspira a tus viajeros justo después del buscador con fotografías y mensajes propios. Puedes cambiar las imágenes y los textos o esconder la sección.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={<Checkbox checked={config.homeHighlightsEnabled ?? true} onChange={(event) => setConfig({ ...config, homeHighlightsEnabled: event.target.checked })} />}
                    label="Mostrar sección en el inicio"
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Frase superior" inputProps={{ maxLength: 60 }} value={config.homeHighlightsEyebrow ?? "VIAJA A TU MANERA"} onChange={(event) => setConfig({ ...config, homeHighlightsEyebrow: event.target.value })} />
                    </Grid>
                      <Grid item xs={12} md={5}>
                      <TextField fullWidth label="Título de la sección" inputProps={{ maxLength: 120 }} value={config.homeHighlightsTitle ?? "Tu viaje empieza con un buen plan"} onChange={(event) => setConfig({ ...config, homeHighlightsTitle: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <TextField fullWidth multiline minRows={2} label="Descripción" inputProps={{ maxLength: 240 }} value={config.homeHighlightsDescription ?? "Explora opciones y recibe confirmación de disponibilidad y precio directamente de tu agencia."} onChange={(event) => setConfig({ ...config, homeHighlightsDescription: event.target.value })} />
                    </Grid>
                    {(config.homeHighlights || defaultHomeHighlights).map((highlight, index) => (
                      <Grid item xs={12} md={4} key={index}>
                        <Box sx={{ height: "100%", p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1.25 }}>Tarjeta {index + 1}</Typography>
                          <Stack spacing={1.5}>
                            <StorageImageCropField
                              label={`Imagen de la tarjeta ${index + 1}`}
                              helperText="Foto panorámica que aparecerá como fondo. Si la quitas, se usará una imagen de viaje predeterminada."
                              value={highlight.imageUrl}
                              aspectRatio={1.45}
                              uploading={experienceStorage.loading}
                              uploadProgress={experienceStorage.progress}
                              onUpload={(file) => experienceStorage.uploadAssetAndGetUrl(file)}
                              onChange={(imageUrl) => {
                                const next = [...(config.homeHighlights || defaultHomeHighlights)];
                                next[index] = { ...next[index], imageUrl };
                                setConfig({ ...config, homeHighlights: next });
                              }}
                            />
                            <TextField fullWidth label="Título" inputProps={{ maxLength: 80 }} value={highlight.title} onChange={(event) => {
                              const next = [...(config.homeHighlights || defaultHomeHighlights)];
                              next[index] = { ...next[index], title: event.target.value };
                              setConfig({ ...config, homeHighlights: next });
                            }} />
                            <TextField fullWidth multiline minRows={2} label="Texto" inputProps={{ maxLength: 180 }} value={highlight.description} onChange={(event) => {
                              const next = [...(config.homeHighlights || defaultHomeHighlights)];
                              next[index] = { ...next[index], description: event.target.value };
                              setConfig({ ...config, homeHighlights: next });
                            }} />
                          </Stack>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}

        {tab === "pages" && selectedPage && (
          <Card sx={travelSectionCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
                  <Box>
                    <Typography variant="h6">Páginas internas</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Edita el contenido base que también se renderiza en la experiencia pública.
                    </Typography>
                  </Box>
                  <TextField
                    select
                    label="Página"
                    value={selectedPageId}
                    onChange={(event) => setSelectedPageId(event.target.value as StorePageId)}
                    sx={{ minWidth: { xs: "100%", md: 220 } }}
                  >
                    {pages.map((page) => (
                      <MenuItem key={page.id} value={page.id}>
                        {experiencePageLabels[page.id]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Nombre de la página" helperText="Nombre de referencia y texto de respaldo si no defines etiquetas para el encabezado o el pie." value={selectedPage.title} onChange={(event) => updateSelectedPage({ title: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Dirección de esta página" helperText={`Es la ruta después del dominio. Ejemplo: ${selectedPage.slug ? `tudominio.com/${selectedPage.slug}` : "tudominio.com/"}.`} value={selectedPage.slug} onChange={(event) => updateSelectedPage({ slug: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Título dentro de la página" helperText="Encabezado grande que aparece al abrir esta página interna." value={selectedPage.contentTitle || ""} onChange={(event) => updateSelectedPage({ contentTitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Texto del enlace en el encabezado" helperText="Nombre corto que verá el visitante en el menú superior." value={selectedPage.navLabel || ""} onChange={(event) => updateSelectedPage({ navLabel: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Texto del enlace en el pie" helperText="Nombre que verá el visitante en el menú inferior de la página." value={selectedPage.footerLabel || ""} onChange={(event) => updateSelectedPage({ footerLabel: event.target.value })} />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 2 }}>
                      <FormControlLabel control={<Checkbox checked={selectedPage.isEnabled} onChange={(event) => updateSelectedPage({ isEnabled: event.target.checked })} />} label="Página publicada y disponible" />
                      <FormControlLabel control={<Checkbox checked={selectedPage.showInNavbar} onChange={(event) => updateSelectedPage({ showInNavbar: event.target.checked })} />} label="Mostrar enlace en el encabezado" />
                      <FormControlLabel control={<Checkbox checked={selectedPage.showInFooter} onChange={(event) => updateSelectedPage({ showInFooter: event.target.checked })} />} label="Mostrar enlace en el pie de página" />
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={5} label="Texto de esta página" helperText="Contenido que verá el visitante debajo del título. Puedes incluir horarios, información de la agencia, condiciones o políticas." value={selectedPage.contentBody || ""} onChange={(event) => updateSelectedPage({ contentBody: event.target.value })} />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        )}

        {tab === "seo" && (
          <Card sx={travelSectionCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6">SEO y metadatos</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Define el título, descripción y nombre de pestaña para la experiencia pública.
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Título para buscadores" helperText="Título que los buscadores pueden mostrar en los resultados. Si queda vacío, se usa el nombre de la agencia." value={config.seoTitle || ""} onChange={(event) => setConfig({ ...config, seoTitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={3} label="Descripción para buscadores" helperText="Resumen que puede aparecer debajo del título en Google y otros buscadores." value={config.seoDescription || ""} onChange={(event) => setConfig({ ...config, seoDescription: event.target.value })} />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        )}

        {tab === "locations" && (
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" startIcon={<Add />} disabled={Boolean(activeAction)} onClick={() => { setEditingLocationId(undefined); setLocationDraft({ country: "Venezuela", city: "", isOrigin: true, isDestination: true, isActive: true }); setLocationDialog(true); }}>Agregar ciudad</Button>
            </Stack>
            <FilterableDataTable<TravelLocation>
              ariaLabel="Rutas y ciudades"
              rows={locations}
              getRowId={(location) => location.id}
              emptyMessage="No hay ciudades que coincidan con esos filtros."
              minWidth={760}
              columns={[
                { id: "country", label: "País", getValue: (location) => location.country },
                { id: "city", label: "Ciudad", getValue: (location) => location.city },
                { id: "usage", label: "Disponible como", getValue: (location) => [location.isOrigin && "Origen", location.isDestination && "Destino / servicio"].filter(Boolean).join(" · ") || "Sin uso" },
                { id: "status", label: "Estado", filter: "select", filterOptions: [{ label: "Activa", value: "Activa" }, { label: "Inactiva", value: "Inactiva" }], getValue: (location) => location.isActive ? "Activa" : "Inactiva", render: (location) => <Chip size="small" label={location.isActive ? "Activa" : "Inactiva"} color={location.isActive ? "success" : "default"} /> },
                { id: "actions", label: "Acciones", filter: "none", sortable: false, align: "right", minWidth: 110, render: (location) => <><IconButton aria-label={`Editar ${location.city}`} disabled={Boolean(activeAction)} onClick={() => { setEditingLocationId(location.id); setLocationDraft({ ...location }); setLocationDialog(true); }}><Edit /></IconButton><IconButton aria-label={`Eliminar ${location.city}`} color="error" disabled={Boolean(activeAction)} onClick={() => void removeLocation(location)}>{activeAction === `location-delete:${location.id}` ? <CircularProgress size={20} color="inherit" aria-label="Eliminando ciudad" /> : <Delete />}</IconButton></> },
              ] satisfies ReadonlyArray<FilterableDataTableColumn<TravelLocation>>}
            />
          </Stack>
        )}

        {tab === "services" && (
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" startIcon={<Add />} disabled={Boolean(activeAction)} onClick={() => { setEditingServiceId(undefined); setServiceDraft({ category: "flight", name: "", description: "", price: 0, flightSchedules: [newFlightSchedule()], isActive: true }); setServiceDialog(true); }}>Crear oferta</Button>
            </Stack>
            <FilterableDataTable<TravelService>
              ariaLabel="Ofertas de viaje"
              rows={services.filter((service) => service.category === "flight" || service.category === "hotel")}
              getRowId={(service) => service.id}
              emptyMessage="No hay ofertas que coincidan con esos filtros."
              minWidth={1050}
              columns={[
                { id: "type", label: "Tipo", filter: "select", filterOptions: [{ label: "Vuelo", value: "Vuelo" }, { label: "Hotel", value: "Hotel" }], getValue: (service) => service.category === "flight" ? "Vuelo" : "Hotel" },
                { id: "name", label: "Oferta", getValue: (service) => service.name },
                { id: "destination", label: "Ruta / destino", getValue: (service) => { const origin = locations.find((location) => location.id === service.originLocationId); const destination = locations.find((location) => location.id === service.destinationLocationId); return service.category === "flight" ? `${origin?.city || "Origen"} → ${destination?.city || "Destino"}` : `${destination?.city || "Destino"}, ${destination?.country || ""}`; } },
                { id: "price", label: "Precio desde", getValue: (service) => Number(service.price || 0), render: (service) => `$${Number(service.price || 0).toLocaleString("es-VE")} / ${service.priceUnit === "per_person" ? "persona" : service.priceUnit === "per_day" ? "día" : "noche"}` },
                { id: "availability", label: "Fechas / disponibilidad", getValue: (service) => service.category === "flight" ? `${service.flightSchedules?.filter((schedule) => schedule.scheduleMode === "weekly").length || 0} frecuencias semanales · ${service.flightSchedules?.filter((schedule) => schedule.scheduleMode !== "weekly").length || 0} fechas puntuales` : `${service.availabilityRanges?.length || 0} períodos` },
                { id: "status", label: "Estado", filter: "select", filterOptions: [{ label: "Activa", value: "Activa" }, { label: "Inactiva", value: "Inactiva" }], getValue: (service) => service.isActive ? "Activa" : "Inactiva", render: (service) => <Chip size="small" label={service.isActive ? "Activa" : "Inactiva"} color={service.isActive ? "success" : "default"} /> },
                { id: "actions", label: "Acciones", filter: "none", sortable: false, align: "right", minWidth: 110, render: (service) => <><IconButton aria-label={`Editar ${service.name}`} disabled={Boolean(activeAction)} onClick={() => { setEditingServiceId(service.id); setServiceDraft({ ...service }); setServiceDialog(true); }}><Edit /></IconButton><IconButton aria-label={`Eliminar ${service.name}`} color="error" disabled={Boolean(activeAction)} onClick={() => void removeService(service)}>{activeAction === `service-delete:${service.id}` ? <CircularProgress size={20} color="inherit" aria-label="Eliminando oferta" /> : <Delete />}</IconButton></> },
              ] satisfies ReadonlyArray<FilterableDataTableColumn<TravelService>>}
            />
          </Stack>
        )}

        {tab === "packages" && (
          <Stack spacing={2}>
                <Typography variant="subtitle1" fontWeight={700}>Ofertas que pueden formar parte de un paquete</Typography>
                <FilterableDataTable<TravelService>
                  ariaLabel="Servicios disponibles para paquetes"
                  rows={services.filter((service) => service.category === "flight" || service.category === "hotel")}
                  getRowId={(service) => service.id}
                  emptyMessage="Todavía no hay vuelos ni hoteles para combinar. Crea una oferta para que pueda aparecer aquí."
                  minWidth={900}
                  columns={[
                    { id: "type", label: "Tipo", filter: "select", filterOptions: [{ label: "Vuelo", value: "Vuelo" }, { label: "Hotel", value: "Hotel" }], getValue: (service) => service.category === "flight" ? "Vuelo" : "Hotel" },
                    { id: "name", label: "Oferta", getValue: (service) => service.name },
                    { id: "destination", label: "Ruta / destino", getValue: (service) => { const origin = locations.find((location) => location.id === service.originLocationId); const destination = locations.find((location) => location.id === service.destinationLocationId); return service.category === "flight" ? `${origin?.city || "Origen"} → ${destination?.city || "Destino"}` : `${destination?.city || "Destino"}, ${destination?.country || ""}`; } },
                    { id: "availability", label: "Fechas disponibles", getValue: (service) => service.category === "flight" ? `${service.flightSchedules?.filter((schedule) => schedule.scheduleMode === "weekly").length || 0} frecuencias semanales · ${service.flightSchedules?.filter((schedule) => schedule.scheduleMode !== "weekly").length || 0} fechas puntuales` : `${service.availabilityRanges?.length || 0} períodos` },
                    { id: "price", label: "Precio orientativo", getValue: (service) => Number(service.price || 0), render: (service) => `$${Number(service.price || 0).toLocaleString("es-VE")} / ${service.priceUnit === "per_person" ? "persona" : "noche"}` },
                    { id: "status", label: "Estado", filter: "select", filterOptions: [{ label: "Activa", value: "Activa" }, { label: "Inactiva", value: "Inactiva" }], getValue: (service) => service.isActive ? "Activa" : "Inactiva", render: (service) => <Chip size="small" label={service.isActive ? "Activa" : "Inactiva"} color={service.isActive ? "success" : "default"} /> },
                  ] satisfies ReadonlyArray<FilterableDataTableColumn<TravelService>>}
                />
                <Button variant="outlined" onClick={() => { setTab("services"); navigate(tabPaths.services); }}>Administrar ofertas</Button>
          </Stack>
        )}
        {tab === "domains" && (
          <Card sx={travelSectionCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="h6">Dominios</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Publica la experiencia en subdominio LYPRO o en un dominio propio.
                    </Typography>
                  </Box>
                  <Button variant="contained" size="small" startIcon={<Domain />} disabled={Boolean(activeAction)} onClick={() => setDomainDialogOpen(true)}>
                    Agregar dominio
                  </Button>
                </Stack>
                <Alert severity="info">
                  Para usar el dominio raíz, asígnalo en Cloud Run al servicio experience-sites y publica en GoDaddy todos los registros A/AAAA que Cloud Run indique. También puedes usar un CNAME a store.lyproai.com si ya tienes ese enrutamiento. Luego pulsa Verificar.
                </Alert>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Hostname</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {domains.map((domain) => (
                      <TableRow key={domain.hostname}>
                        <TableCell>{domain.hostname}</TableCell>
                        <TableCell>
                          <Chip size="small" label={domain.status} color={domainStatusColor(domain.status)} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" disabled={loadingData || Boolean(activeAction)} onClick={() => openTravelPreview(domain.hostname)}>Probar página</Button>
                            <LoadingButton size="small" loading={activeAction === `domain-verify:${domain.hostname}`} disabled={Boolean(activeAction && activeAction !== `domain-verify:${domain.hostname}`)} onClick={() => void verifyDomain(domain.hostname)} loadingLabel="Verificando...">Verificar</LoadingButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Stack>
            </CardContent>
          </Card>
        )}

        {tab === "bookings" && (
          <Stack spacing={2}>
                {bookings.length === 0 ? (
                  <Alert severity="info">{loadingData ? "Cargando solicitudes de viajeros..." : "Aún no hay solicitudes de viajeros. Cuando alguien consulte una oferta desde tu página, aparecerá aquí."}</Alert>
                ) : (
                  <FilterableDataTable<TravelBookingRequest>
                    ariaLabel="Solicitudes de reserva de viajes"
                    rows={bookings}
                    getRowId={(booking) => booking.id}
                    emptyMessage="No hay solicitudes que coincidan con esos filtros."
                    minWidth={1150}
                    columns={[
                      { id: "reference", label: "Código", getValue: (booking) => booking.publicReference, minWidth: 125 },
                      { id: "offer", label: "Oferta o paquete", getValue: (booking) => `${booking.packageName} ${booking.bundleItems?.map((item) => item.name).join(" ") || ""}`, render: (booking) => <Stack spacing={0.25}><Typography variant="body2">{booking.packageName}</Typography>{booking.bundleItems?.length ? <Typography variant="caption" color="text.secondary">{booking.bundleItems.map((item) => item.name).join(" · ")}</Typography> : null}</Stack>, minWidth: 180 },
                      { id: "customer", label: "Cliente / teléfono", getValue: (booking) => `${booking.customerName} ${booking.customerPhone}`, render: (booking) => <Stack spacing={0.25}><Typography variant="body2">{booking.customerName}</Typography><Typography variant="caption" color="text.secondary">{booking.customerPhone}</Typography></Stack>, minWidth: 180 },
                      { id: "travelers", label: "Pasajeros", getValue: (booking) => `${booking.travelers} viajeros ${booking.adults ?? ""} adultos ${booking.children || 0} niños ${booking.infants || 0} bebés`, render: (booking) => <Stack spacing={0.25}><Typography variant="body2">{booking.travelers} en total</Typography><Typography variant="caption" color="text.secondary">{booking.adults === undefined ? "Desglose no registrado" : `${booking.adults} adultos · ${booking.children || 0} niños · ${booking.infants || 0} bebés`}</Typography></Stack>, minWidth: 170 },
                      { id: "dates", label: "Fechas", getValue: (booking) => `${booking.startDate} ${booking.endDate || ""} ${booking.airlineName || ""}`, render: (booking) => <Stack spacing={0.25}><Typography variant="body2">{booking.productCategory === "flight" ? booking.flightTripType === "one_way" ? `${booking.startDate} · Solo ida` : `${booking.startDate} → ${booking.endDate || "Sin regreso"}` : booking.productCategory === "hotel" || booking.productCategory === "inn" ? `Entrada ${booking.startDate} · Salida ${booking.endDate}` : booking.productCategory === "car" ? `Recogida ${booking.startDate} · Devolución ${booking.endDate}` : `Inicio ${booking.startDate} · Fin ${booking.endDate}`}</Typography>{booking.airlineName ? <Typography variant="caption" color="text.secondary">{booking.airlineName}</Typography> : null}</Stack>, minWidth: 180 },
                      { id: "status", label: "Estado", filter: "select", filterOptions: Object.entries(bookingStatusLabels).map(([value, label]) => ({ value, label })), getValue: (booking) => booking.status, render: (booking) => <Stack direction="row" spacing={1} alignItems="center"><TextField select size="small" value={booking.status} disabled={Boolean(activeAction)} sx={{ minWidth: { xs: 145, sm: 180 } }} onChange={(event) => void updateBookingStatus(booking.id, event.target.value as TravelBookingStatus)}>{Object.entries(bookingStatusLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>{activeAction === `booking-status:${booking.id}` ? <CircularProgress size={16} aria-label="Actualizando estado" /> : null}</Stack>, minWidth: 210 },
                      { id: "quote", label: "Cotización", filter: "none", sortable: false, align: "right", minWidth: 135, render: (booking) => <Button size="small" startIcon={<AttachMoney />} disabled={Boolean(activeAction)} onClick={() => openQuoteDialog(booking)}>{booking.quote ? "Editar" : "Cotizar"}</Button> },
                    ] satisfies ReadonlyArray<FilterableDataTableColumn<TravelBookingRequest>>}
                  />
                )}
          </Stack>
        )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={locationDialog} onClose={() => { if (!activeAction) setLocationDialog(false); }} fullWidth maxWidth="sm">
        <DialogTitle>{editingLocationId ? "Editar ciudad" : "Agregar ciudad"}</DialogTitle><DialogContent dividers><Stack spacing={2} sx={{ pt: 1 }}>
          <TextField fullWidth label="País" value={locationDraft.country || ""} onChange={(event) => setLocationDraft({ ...locationDraft, country: event.target.value })} />
          <TextField fullWidth label="Ciudad" value={locationDraft.city || ""} onChange={(event) => setLocationDraft({ ...locationDraft, city: event.target.value })} />
          <FormControlLabel control={<Checkbox checked={Boolean(locationDraft.isOrigin)} onChange={(event) => setLocationDraft({ ...locationDraft, isOrigin: event.target.checked })} />} label="Permitir como origen" />
          <FormControlLabel control={<Checkbox checked={Boolean(locationDraft.isDestination)} onChange={(event) => setLocationDraft({ ...locationDraft, isDestination: event.target.checked })} />} label="Permitir como destino y ciudad de servicio" />
          <FormControlLabel control={<Checkbox checked={locationDraft.isActive !== false} onChange={(event) => setLocationDraft({ ...locationDraft, isActive: event.target.checked })} />} label="Ciudad activa en el buscador" />
        </Stack></DialogContent><DialogActions><Button disabled={Boolean(activeAction)} onClick={() => setLocationDialog(false)}>Cancelar</Button><LoadingButton variant="contained" loading={activeAction === "location-save"} disabled={Boolean(activeAction && activeAction !== "location-save")} onClick={() => void saveLocation()} loadingLabel="Guardando ciudad...">Guardar ciudad</LoadingButton></DialogActions>
      </Dialog>

      <Dialog open={serviceDialog} onClose={() => { if (!activeAction) setServiceDialog(false); }} fullWidth maxWidth="md">
        <DialogTitle>{editingServiceId ? "Editar oferta" : "Crear oferta"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Categoría fija" value={serviceDraft.category || "flight"} onChange={(event) => {
                const category = event.target.value as TravelServiceCategory;
                setServiceDraft({ ...serviceDraft, category, originLocationId: "", flightSchedules: category === "flight" ? [newFlightSchedule()] : [], availabilityRanges: category === "flight" || category === "car" ? [] : [newAvailabilityRange()], ...(category === "car" ? { vehicleType: serviceDraft.vehicleType || "sedan", passengerCapacity: serviceDraft.passengerCapacity || 4, fleetQuantity: serviceDraft.fleetQuantity || 1 } : {}) });
              }}>
                <MenuItem value="flight">Vuelo</MenuItem><MenuItem value="hotel">Hotel</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label={serviceDraft.category === "flight" ? "Nombre de la ruta" : serviceDraft.category === "car" ? "Nombre de esta categoría de flota" : "Nombre de la oferta"} helperText={serviceDraft.category === "car" ? "Usa un nombre genérico, no un modelo de carro específico." : undefined} value={serviceDraft.name || ""} onChange={(event) => setServiceDraft({ ...serviceDraft, name: event.target.value })} /></Grid>
            {serviceDraft.category === "flight" && <Grid item xs={12} md={6}><TextField select fullWidth label="Origen" value={serviceDraft.originLocationId || ""} onChange={(event) => setServiceDraft({ ...serviceDraft, originLocationId: event.target.value })}>{locations.filter((item) => item.isActive && item.isOrigin).map((item) => <MenuItem key={item.id} value={item.id}>{item.city}, {item.country}</MenuItem>)}</TextField></Grid>}
            <Grid item xs={12} md={serviceDraft.category === "flight" ? 6 : 12}><TextField select fullWidth label="Destino o ciudad donde opera" helperText={serviceDraft.category === "car" ? "Esta ciudad define dónde aparecerá esta categoría de carros en el buscador." : undefined} value={serviceDraft.destinationLocationId || ""} onChange={(event) => setServiceDraft({ ...serviceDraft, destinationLocationId: event.target.value })}>{locations.filter((item) => item.isActive && item.isDestination).map((item) => <MenuItem key={item.id} value={item.id}>{item.city}, {item.country}</MenuItem>)}</TextField></Grid>

            {serviceDraft.category === "flight" && <Grid item xs={12}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Frecuencias y tarifas de esta ruta</Typography>
                  <Typography variant="body2" color="text.secondary">Configura origen y destino una sola vez. Agrega aquí las aerolíneas y sus días o fechas; cada frecuencia puede servir para solo ida y para ida y vuelta.</Typography>
                </Box>
                {(serviceDraft.flightSchedules || []).map((schedule, index) => {
                  const isWeekly = schedule.scheduleMode === "weekly";
                  const isRoundTrip = isWeekly ? Boolean(schedule.returnDaysOfWeek?.length) : Boolean(schedule.returnDate);
                  const updateSchedule = (changes: Partial<TravelFlightSchedule>) => setServiceDraft((current) => ({
                    ...current,
                    flightSchedules: (current.flightSchedules || []).map((item) => item.id === schedule.id ? { ...item, ...changes } : item),
                  }));
                  const weekdayPicker = (label: string, selectedDays: number[], onChange: (days: number[]) => void) => <Stack spacing={0.75}>
                    <Typography variant="body2" fontWeight={600}>{label}</Typography>
                    <ToggleButtonGroup
                      size="small"
                      value={selectedDays}
                      aria-label={label}
                      onChange={(_, value: number[]) => onChange(value)}
                      sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, "& .MuiToggleButtonGroup-grouped": { border: "1px solid", borderColor: "divider", borderRadius: "8px !important", mx: "0 !important" } }}
                    >
                      {travelWeekdays.map((day) => <ToggleButton key={day.value} value={day.value} aria-label={day.label} sx={{ px: 1.15, py: 0.6, textTransform: "none" }}>{day.shortLabel}</ToggleButton>)}
                    </ToggleButtonGroup>
                  </Stack>;
                  return <Card key={schedule.id || index} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item xs={12} sm={4}><TextField fullWidth label="Aerolínea" value={schedule.airlineName} onChange={(event) => updateSchedule({ airlineName: event.target.value })} /></Grid>
                      <Grid item xs={12} sm={4}><TextField select fullWidth label="Disponibilidad" value={isWeekly ? "weekly" : "dates"} onChange={(event) => updateSchedule({ scheduleMode: event.target.value as "weekly" | "dates" })}><MenuItem value="weekly">Frecuencia semanal</MenuItem><MenuItem value="dates">Fechas puntuales</MenuItem></TextField></Grid>
                      <Grid item xs={9} sm={3}><TextField fullWidth type="number" inputProps={{ min: 0, step: "0.01" }} label={isRoundTrip ? "Precio ida y vuelta USD" : "Precio solo ida USD"} value={schedule.price} onChange={(event) => updateSchedule({ price: Number(event.target.value) })} helperText="Precio base por persona" /></Grid>
                      <Grid item xs={3} sm={1}><IconButton aria-label={`Quitar tarifa de ${schedule.airlineName || `aerolínea ${index + 1}`}`} disabled={(serviceDraft.flightSchedules || []).length <= 1} onClick={() => setServiceDraft((current) => ({ ...current, flightSchedules: (current.flightSchedules || []).filter((item) => item.id !== schedule.id) }))}><Delete /></IconButton></Grid>
                      {isRoundTrip ? <Grid item xs={12} sm={6}><TextField fullWidth type="number" inputProps={{ min: 0, step: "0.01" }} label="Precio solo ida USD (opcional)" value={schedule.oneWayPrice ?? ""} onChange={(event) => updateSchedule({ oneWayPrice: event.target.value === "" ? undefined : Number(event.target.value) })} helperText="Si lo dejas vacío, se usa el precio base también para solo ida." /></Grid> : null}
                      {isWeekly ? <>
                        <Grid item xs={12} md={6}>{weekdayPicker("Días de salida (ida)", schedule.departureDaysOfWeek || [], (days) => updateSchedule({ departureDaysOfWeek: days }))}</Grid>
                        <Grid item xs={12} md={6}>{weekdayPicker("Días de regreso (opcional)", schedule.returnDaysOfWeek || [], (days) => updateSchedule({ returnDaysOfWeek: days }))}</Grid>
                        <Grid item xs={12}><Typography variant="caption" color="text.secondary">Los días de ida habilitan ambas modalidades. Configura los días de regreso para ofrecer ida y vuelta; el precio de solo ida se puede ajustar arriba sin duplicar la ruta.</Typography></Grid>
                      </> : <>
                        <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Fecha de ida" InputLabelProps={{ shrink: true }} inputProps={{ min: new Date().toISOString().slice(0, 10) }} value={schedule.departureDate} onChange={(event) => updateSchedule({ departureDate: event.target.value, returnDate: schedule.returnDate < event.target.value ? "" : schedule.returnDate })} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Fecha de vuelta (opcional)" InputLabelProps={{ shrink: true }} inputProps={{ min: schedule.departureDate || new Date().toISOString().slice(0, 10) }} value={schedule.returnDate} onChange={(event) => updateSchedule({ returnDate: event.target.value })} /></Grid>
                      </>}
                    </Grid>
                  </Card>;
                })}
                <Button startIcon={<Add />} onClick={() => setServiceDraft((current) => ({ ...current, flightSchedules: [...(current.flightSchedules || []), newFlightSchedule()] }))}>Agregar aerolínea</Button>
              </Stack>
            </Grid>}

            {(serviceDraft.category === "hotel" || serviceDraft.category === "inn" || serviceDraft.category === "car") && <Grid item xs={12}>
              <Stack spacing={1.5}>
                <Box><Typography variant="subtitle1" fontWeight={700}>{serviceDraft.category === "car" ? "Disponibilidad de la flota" : "Períodos disponibles"}</Typography><Typography variant="body2" color="text.secondary">{serviceDraft.category === "car" ? "Puedes dejarlo vacío si esta categoría opera todo el año." : "La página solo permitirá elegir estadías que queden dentro de uno de estos períodos."}</Typography></Box>
                {(serviceDraft.availabilityRanges || []).map((range, index) => <Grid container spacing={1.25} alignItems="center" key={range.id || index}>
                  <Grid item xs={5}><TextField fullWidth type="date" label="Disponible desde" InputLabelProps={{ shrink: true }} inputProps={{ min: new Date().toISOString().slice(0, 10) }} value={range.startDate} onChange={(event) => setServiceDraft((current) => ({ ...current, availabilityRanges: (current.availabilityRanges || []).map((item) => item.id === range.id ? { ...item, startDate: event.target.value, endDate: item.endDate < event.target.value ? "" : item.endDate } : item) }))} /></Grid>
                  <Grid item xs={5}><TextField fullWidth type="date" label="Disponible hasta" InputLabelProps={{ shrink: true }} inputProps={{ min: range.startDate || new Date().toISOString().slice(0, 10) }} value={range.endDate} onChange={(event) => setServiceDraft((current) => ({ ...current, availabilityRanges: (current.availabilityRanges || []).map((item) => item.id === range.id ? { ...item, endDate: event.target.value } : item) }))} /></Grid>
                  <Grid item xs={2}><IconButton aria-label={`Quitar período ${index + 1}`} disabled={(serviceDraft.availabilityRanges || []).length <= 1} onClick={() => setServiceDraft((current) => ({ ...current, availabilityRanges: (current.availabilityRanges || []).filter((item) => item.id !== range.id) }))}><Delete /></IconButton></Grid>
                </Grid>)}
                <Button startIcon={<Add />} onClick={() => setServiceDraft((current) => ({ ...current, availabilityRanges: [...(current.availabilityRanges || []), newAvailabilityRange()] }))}>Agregar período</Button>
              </Stack>
            </Grid>}

            {serviceDraft.category === "car" && <>
              <Grid item xs={12} md={4}><TextField select fullWidth label="Tamaño de carro" value={serviceDraft.vehicleType || "sedan"} onChange={(event) => setServiceDraft({ ...serviceDraft, vehicleType: event.target.value as TravelVehicleType })}>{travelVehicleOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth type="number" inputProps={{ min: 1, max: 20, step: 1 }} label="Pasajeros por carro" value={serviceDraft.passengerCapacity ?? 4} onChange={(event) => setServiceDraft({ ...serviceDraft, passengerCapacity: Number(event.target.value) })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth type="number" inputProps={{ min: 1, step: 1 }} label="Cantidad disponible en la flota" value={serviceDraft.fleetQuantity ?? 1} onChange={(event) => setServiceDraft({ ...serviceDraft, fleetQuantity: Number(event.target.value) })} /></Grid>
            </>}

            {serviceDraft.category !== "flight" && <Grid item xs={12} md={6}><TextField fullWidth type="number" inputProps={{ min: 0, step: "0.01" }} label="Precio desde en USD" value={serviceDraft.price ?? 0} onChange={(event) => setServiceDraft({ ...serviceDraft, price: Number(event.target.value) })} helperText={serviceDraft.category === "car" ? "Precio diario para esta categoría de flota." : "Precio por noche."} /></Grid>}
            <Grid item xs={12} md={6}><TextField select fullWidth label="Estado" value={serviceDraft.isActive === false ? "inactive" : "active"} onChange={(event) => setServiceDraft({ ...serviceDraft, isActive: event.target.value === "active" })}><MenuItem value="active">Activa y visible</MenuItem><MenuItem value="inactive">Inactiva</MenuItem></TextField></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Descripción para el viajero" value={serviceDraft.description || ""} onChange={(event) => setServiceDraft({ ...serviceDraft, description: event.target.value })} /></Grid>
            <Grid item xs={12}>
              <StorageImageCropField label={serviceDraft.category === "car" ? "Imagen de referencia de esta categoría" : "Imagen de la oferta"} helperText={serviceDraft.category === "car" ? "Sube una imagen representativa del tamaño de carro; no tiene que ser el modelo exacto." : "Se muestra en la tarjeta pública de esta oferta."} value={serviceDraft.imageUrl} aspectRatio={1.45} uploading={experienceStorage.loading} uploadProgress={experienceStorage.progress} onUpload={(file) => experienceStorage.uploadAssetAndGetUrl(file)} onChange={(imageUrl) => setServiceDraft({ ...serviceDraft, imageUrl })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button disabled={Boolean(activeAction)} onClick={() => setServiceDialog(false)}>Cancelar</Button><LoadingButton variant="contained" loading={activeAction === "service-save"} disabled={Boolean(activeAction && activeAction !== "service-save")} onClick={() => void saveService()} loadingLabel="Guardando oferta...">Guardar oferta</LoadingButton></DialogActions>
      </Dialog>

      <Dialog open={categoryDialogOpen} onClose={() => { if (!activeAction) resetCategoryDialog(); }} fullWidth maxWidth="sm">
        <DialogTitle>{categoryEditingId ? "Editar categoría" : "Crear categoría"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField fullWidth label="Nombre" value={categoryDraft.name || ""} onChange={(event) => setCategoryDraft({ ...categoryDraft, name: event.target.value })} />
            <TextField fullWidth label="Descripción" multiline minRows={3} value={categoryDraft.description || ""} onChange={(event) => setCategoryDraft({ ...categoryDraft, description: event.target.value })} />
            <TextField select fullWidth label="Estado" value={categoryDraft.isActive === false ? "inactive" : "active"} onChange={(event) => setCategoryDraft({ ...categoryDraft, isActive: event.target.value === "active" })}>
              <MenuItem value="active">Activa</MenuItem>
              <MenuItem value="inactive">Inactiva</MenuItem>
            </TextField>
            <TextField fullWidth type="number" label="Orden" value={categoryDraft.sortOrder ?? 0} onChange={(event) => setCategoryDraft({ ...categoryDraft, sortOrder: Number(event.target.value) })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={Boolean(activeAction)} onClick={resetCategoryDialog}>
            Cancelar
          </Button>
          <LoadingButton variant="contained" loading={activeAction === "category-save"} disabled={Boolean(activeAction && activeAction !== "category-save")} onClick={() => void saveCategory()} loadingLabel="Guardando categoría...">
            Guardar
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={packageDialogOpen} onClose={() => { if (!activeAction) resetPackageDialog(); }} fullWidth maxWidth="md">
        <DialogTitle>{packageEditingId ? "Editar paquete" : "Crear paquete"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Nombre" value={packageDraft.name || ""} onChange={(event) => setPackageDraft({ ...packageDraft, name: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Origen (si incluye vuelo)" value={packageDraft.originLocationId || ""} helperText="Los paquetes con vuelos necesitan un único origen. En paquetes sin vuelos, puedes dejarlo vacío." onChange={(event) => setPackageDraft((current) => ({ ...current, originLocationId: event.target.value, serviceIds: (current.serviceIds || []).filter((id) => { const service = services.find((item) => item.id === id); return !service || service.category !== "flight" || !event.target.value || service.originLocationId === event.target.value; }) }))}>
                <MenuItem value="">Sin origen fijo / cualquier origen</MenuItem>
                {locations.filter((item) => item.isActive && item.isOrigin).map((item) => <MenuItem key={item.id} value={item.id}>{item.city}, {item.country}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Destino" value={packageDraft.destinationLocationId || ""} onChange={(event) => { const destinationLocationId = event.target.value; const place = locations.find((item) => item.id === destinationLocationId); setPackageDraft((current) => ({ ...current, destinationLocationId, destination: place?.city || "", serviceIds: (current.serviceIds || []).filter((id) => { const service = services.find((item) => item.id === id); return !service || !destinationLocationId || service.destinationLocationId === destinationLocationId; }) })); }}>
                {locations.filter((item) => item.isActive && item.isDestination).map((item) => <MenuItem key={item.id} value={item.id}>{item.city}, {item.country}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label="Descripción" value={packageDraft.description || ""} onChange={(event) => setPackageDraft({ ...packageDraft, description: event.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth SelectProps={{ multiple: true }} label="Servicios incluidos" helperText="Elige ofertas activas para el destino. Si agregas vuelos, todos deben salir de la misma ciudad y el origen se completa automáticamente." value={packageDraft.serviceIds || []} onChange={(event) => { const serviceIds = event.target.value as unknown as string[]; const flightOrigins = Array.from(new Set(services.filter((service) => serviceIds.includes(service.id) && service.category === "flight").map((service) => service.originLocationId).filter(Boolean))); setPackageDraft((current) => ({ ...current, serviceIds, ...(flightOrigins.length === 1 ? { originLocationId: flightOrigins[0] } : {}) })); }}>
                {services.filter((service) => service.isActive && (!packageDraft.destinationLocationId || service.destinationLocationId === packageDraft.destinationLocationId) && (!packageDraft.originLocationId || service.category !== "flight" || service.originLocationId === packageDraft.originLocationId)).map((service) => <MenuItem key={service.id} value={service.id}>{service.name} · ${Number(service.price).toFixed(2)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Cómo definir el precio" value={packageDraft.priceMode || "fixed"} onChange={(event) => setPackageDraft({ ...packageDraft, priceMode: event.target.value as "fixed" | "discount" })}><MenuItem value="fixed">Precio final del paquete</MenuItem><MenuItem value="discount">Descuento fijo al total</MenuItem></TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label={packageDraft.priceMode === "discount" ? "Descuento USD" : "Precio final USD"} value={packageDraft.priceMode === "discount" ? packageDraft.discountAmount ?? 0 : packageDraft.customPrice ?? packageDraft.priceFrom ?? 0} onChange={(event) => setPackageDraft({ ...packageDraft, ...(packageDraft.priceMode === "discount" ? { discountAmount: Number(event.target.value) } : { customPrice: Number(event.target.value), priceFrom: Number(event.target.value) }) })} />
            </Grid>
            <Grid item xs={12} md={4}><Alert severity="info">Costo base calculado: ${services.filter((service) => packageDraft.serviceIds?.includes(service.id)).reduce((sum, service) => sum + Number(service.price || 0) * (service.category === "car" ? Math.max(1, Number(packageDraft.durationDays || 0)) : service.category === "hotel" || service.category === "inn" ? Math.max(1, Number(packageDraft.durationNights || 0)) : 1), 0).toFixed(2)} USD</Alert></Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="number" label="Dias" value={packageDraft.durationDays ?? 0} onChange={(event) => setPackageDraft({ ...packageDraft, durationDays: Number(event.target.value) })} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="number" label="Noches" value={packageDraft.durationNights ?? 0} onChange={(event) => setPackageDraft({ ...packageDraft, durationNights: Number(event.target.value) })} />
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1.5}>
                <Box><Typography variant="subtitle1" fontWeight={700}>Fechas válidas del paquete</Typography><Typography variant="body2" color="text.secondary">Estas fechas deben coincidir con los vuelos y hospedajes que incluiste. Fuera de estos períodos, el viajero no podrá seleccionarlo.</Typography></Box>
                {(packageDraft.availabilityRanges || []).map((range, index) => <Grid container spacing={1.25} alignItems="center" key={range.id || index}>
                  <Grid item xs={5}><TextField fullWidth type="date" label="Disponible desde" InputLabelProps={{ shrink: true }} inputProps={{ min: new Date().toISOString().slice(0, 10) }} value={range.startDate} onChange={(event) => setPackageDraft((current) => ({ ...current, availabilityRanges: (current.availabilityRanges || []).map((item) => item.id === range.id ? { ...item, startDate: event.target.value, endDate: item.endDate < event.target.value ? "" : item.endDate } : item) }))} /></Grid>
                  <Grid item xs={5}><TextField fullWidth type="date" label="Disponible hasta" InputLabelProps={{ shrink: true }} inputProps={{ min: range.startDate || new Date().toISOString().slice(0, 10) }} value={range.endDate} onChange={(event) => setPackageDraft((current) => ({ ...current, availabilityRanges: (current.availabilityRanges || []).map((item) => item.id === range.id ? { ...item, endDate: event.target.value } : item) }))} /></Grid>
                  <Grid item xs={2}><IconButton aria-label={`Quitar período ${index + 1}`} disabled={(packageDraft.availabilityRanges || []).length <= 1} onClick={() => setPackageDraft((current) => ({ ...current, availabilityRanges: (current.availabilityRanges || []).filter((item) => item.id !== range.id) }))}><Delete /></IconButton></Grid>
                </Grid>)}
                <Button startIcon={<Add />} onClick={() => setPackageDraft((current) => ({ ...current, availabilityRanges: [...(current.availabilityRanges || []), newAvailabilityRange()] }))}>Agregar período</Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <StorageImageCropField label="Imagen del paquete" helperText="Sube la imagen que aparecerá en la tarjeta pública del paquete." value={packageDraft.imageUrl} aspectRatio={1.45} uploading={experienceStorage.loading} uploadProgress={experienceStorage.progress} onUpload={(file) => experienceStorage.uploadAssetAndGetUrl(file)} onChange={(imageUrl) => setPackageDraft({ ...packageDraft, imageUrl })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Incluye (separado por coma)" value={Array.isArray(packageDraft.includes) ? packageDraft.includes.join(", ") : String(packageDraft.includes || "")} onChange={(event) => setPackageDraft({ ...packageDraft, includes: event.target.value as unknown as string[] })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="No incluye (separado por coma)" value={Array.isArray(packageDraft.excludes) ? packageDraft.excludes.join(", ") : String(packageDraft.excludes || "")} onChange={(event) => setPackageDraft({ ...packageDraft, excludes: event.target.value as unknown as string[] })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Disponibilidad manual" value={packageDraft.availabilityNotes || ""} onChange={(event) => setPackageDraft({ ...packageDraft, availabilityNotes: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Capacidad manual" value={packageDraft.capacityNotes || ""} onChange={(event) => setPackageDraft({ ...packageDraft, capacityNotes: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Estado" value={packageDraft.isActive === false ? "inactive" : "active"} onChange={(event) => setPackageDraft({ ...packageDraft, isActive: event.target.value === "active" })}>
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="Orden" value={packageDraft.sortOrder ?? 0} onChange={(event) => setPackageDraft({ ...packageDraft, sortOrder: Number(event.target.value) })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={Boolean(activeAction)} onClick={resetPackageDialog}>
            Cancelar
          </Button>
          <LoadingButton variant="contained" loading={activeAction === "package-save"} disabled={Boolean(activeAction && activeAction !== "package-save")} onClick={() => void savePackage()} loadingLabel="Guardando paquete...">
            Guardar
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={domainDialogOpen} onClose={() => { if (!activeAction) setDomainDialogOpen(false); }} fullWidth maxWidth="sm">
        <DialogTitle>Agregar dominio</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField fullWidth label="Dominio personalizado" placeholder="viajes.midominio.com" value={domainDraft} onChange={(event) => setDomainDraft(event.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={Boolean(activeAction)} onClick={() => setDomainDialogOpen(false)}>
            Cancelar
          </Button>
          <LoadingButton variant="contained" loading={activeAction === "domain-add"} disabled={Boolean(activeAction && activeAction !== "domain-add")} onClick={() => void addDomain()} loadingLabel="Agregando dominio...">
            Agregar
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(quoteBooking)} onClose={() => { if (!activeAction) setQuoteBooking(undefined); }} fullWidth maxWidth="md">
        <DialogTitle>Cotización · {quoteBooking?.publicReference}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {quoteDraft.items.map((item, index) => (
              <Grid container spacing={1} key={`${index}-${item.description}`}>
                <Grid item xs={12} md={6}><TextField fullWidth label="Concepto" value={item.description} onChange={(event) => setQuoteDraft((current) => ({ ...current, items: current.items.map((row, i) => i === index ? { ...row, description: event.target.value } : row) }))} /></Grid>
                <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Cantidad" value={item.quantity} onChange={(event) => setQuoteDraft((current) => ({ ...current, items: current.items.map((row, i) => i === index ? { ...row, quantity: Number(event.target.value) } : row) }))} /></Grid>
                <Grid item xs={6} md={3}><TextField fullWidth type="number" label="Precio unitario USD" value={item.unitPrice} onChange={(event) => setQuoteDraft((current) => ({ ...current, items: current.items.map((row, i) => i === index ? { ...row, unitPrice: Number(event.target.value) } : row) }))} /></Grid>
                <Grid item xs={12} md={1}><IconButton color="error" aria-label="Quitar concepto" onClick={() => setQuoteDraft((current) => ({ ...current, items: current.items.filter((_, i) => i !== index) }))}><Delete /></IconButton></Grid>
              </Grid>
            ))}
            <Button variant="outlined" onClick={() => setQuoteDraft((current) => ({ ...current, items: [...current.items, { id: `${Date.now()}`, description: "", quantity: 1, unitPrice: 0 }] }))}>Agregar concepto</Button>
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}><TextField fullWidth label="Concepto de ajuste" value={quoteDraft.adjustmentLabel || ""} onChange={(event) => setQuoteDraft({ ...quoteDraft, adjustmentLabel: event.target.value })} /></Grid>
              <Grid item xs={6} md={4}><TextField fullWidth type="number" label="Ajuste USD" value={quoteDraft.adjustmentAmount || 0} onChange={(event) => setQuoteDraft({ ...quoteDraft, adjustmentAmount: Number(event.target.value) })} /></Grid>
              <Grid item xs={6} md={4}><TextField fullWidth type="number" label="Descuento USD" value={quoteDraft.discountAmount || 0} onChange={(event) => setQuoteDraft({ ...quoteDraft, discountAmount: Number(event.target.value) })} /></Grid>
              <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Notas para el viajero" value={quoteDraft.notes || ""} onChange={(event) => setQuoteDraft({ ...quoteDraft, notes: event.target.value })} /></Grid>
            </Grid>
            <Alert severity="info">Total de la cotización: <strong>${quotePreviewTotal.toFixed(2)} USD</strong></Alert>
            <FormControlLabel control={<Checkbox checked={sendQuoteAfterSave} onChange={(event) => setSendQuoteAfterSave(event.target.checked)} />} label="Enviar al cliente por WhatsApp al guardar" />
            {sendQuoteAfterSave && <Alert severity="info">Si cerró la ventana de atención de WhatsApp, se requiere una plantilla aprobada para enviar el mensaje.</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions><Button color="inherit" disabled={Boolean(activeAction)} onClick={() => setQuoteBooking(undefined)}>Cancelar</Button><LoadingButton variant="contained" loading={activeAction === "quote-save"} disabled={Boolean(activeAction && activeAction !== "quote-save")} onClick={() => void saveQuote()} loadingLabel={sendQuoteAfterSave ? "Enviando por WhatsApp..." : "Guardando cotización..."}>Guardar cotización</LoadingButton></DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(categoryToDelete)}
        title="Eliminar categoría"
        message={`Se eliminará la categoría ${categoryToDelete?.name || ""}.`}
        confirmColor="error"
        confirmText="Eliminar"
        loading={activeAction === "category-delete"}
        onClose={() => { if (!activeAction) setCategoryToDelete(undefined); }}
        onConfirm={() => void deleteCategory()}
      />

      <ConfirmationDialog
        open={Boolean(packageToDelete)}
        title="Eliminar paquete"
        message={`Se eliminará el paquete ${packageToDelete?.name || ""}.`}
        confirmColor="error"
        confirmText="Eliminar"
        loading={activeAction === "package-delete"}
        onClose={() => { if (!activeAction) setPackageToDelete(undefined); }}
        onConfirm={() => void deletePackage()}
      />

      <FeedbackSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default TravelPage;
